/**
 * Overwatch Intelligence Panel
 * 
 * Provides access to news/social media intelligence from the Overwatch database.
 * Allows users to search for articles relevant to their loaded operation area.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Button,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Link,
  Alert,
  alpha,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Public as GlobeIcon,
  Article as ArticleIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Newspaper as NewsIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  MyLocation as MyLocationIcon,
  Info as InfoIcon,
  CalendarToday as DateIcon,
  Category as CategoryIcon,
  SentimentSatisfied as SentimentIcon,
  Place as PlaceIcon,
  NoteAdd as SaveNoteIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  searchArticles,
  searchByArea,
  searchByRadius,
  checkHealth,
  getArticle,
  translatePlaceNames,
  OverwatchArticle,
  OverwatchSearchResponse,
  calculateBoundingBox,
  formatPubdate,
  getSentimentColor,
  getCategoryColor,
} from '../../services/overwatchApi';
import { PathfinderDataPoint } from '../../services/pathfinderApi';
import { batchReverseGeocode } from '../../utils/geocoding';

interface OverwatchIntelProps {
  dataPoints: PathfinderDataPoint[];
  dwellLocations?: Array<{ latitude: number; longitude: number; [key: string]: unknown }>;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onArticleClick?: (article: OverwatchArticle) => void;
  onZoomToLocation?: (lat: number, lng: number, zoom?: number) => void;
  /** Load articles onto the map */
  onLoadArticlesToMap?: (articles: OverwatchArticle[]) => void;
  /** Articles currently loaded on map */
  loadedArticles?: OverwatchArticle[];
  /** Clear articles from map */
  onClearArticles?: () => void;
  /** Full panel mode (no collapsible header) */
  isFullPanel?: boolean;
  /** Save article to device notes (intel packet) */
  onSaveArticleToNotes?: (article: OverwatchArticle) => Promise<boolean>;
  /** List of devices available for note saving */
  availableDevices?: Array<{ id: string; label: string }>;
}

// Common categories for quick filtering
const COMMON_CATEGORIES = [
  'Crime',
  'Drugs',
  'Military',
  'Security',
  'Terrorism',
  'Border',
  'Violence',
  'Politics',
];

const OverwatchIntel: React.FC<OverwatchIntelProps> = ({
  dataPoints,
  dwellLocations = [],
  collapsed = false,
  onToggleCollapse,
  onArticleClick,
  onZoomToLocation,
  onLoadArticlesToMap,
  loadedArticles = [],
  onClearArticles,
  isFullPanel = false,
  onSaveArticleToNotes,
  availableDevices = [],
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [daysBack, setDaysBack] = useState(30);
  const [searchResults, setSearchResults] = useState<OverwatchSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serviceAvailable, setServiceAvailable] = useState<boolean | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchMode, setSearchMode] = useState<'keyword' | 'area' | 'radius' | 'places'>('keyword');
  const [searchRadius, setSearchRadius] = useState(50); // km
  const [selectedArticle, setSelectedArticle] = useState<OverwatchArticle | null>(null);
  const [fullArticle, setFullArticle] = useState<OverwatchArticle | null>(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [extractedPlaceNames, setExtractedPlaceNames] = useState<string[]>([]);
  const [placeContexts, setPlaceContexts] = useState<Map<string, string>>(new Map());
  const [isExtractingPlaces, setIsExtractingPlaces] = useState(false);
  const [showPlaceContext, setShowPlaceContext] = useState(false);
  const [savingToNotes, setSavingToNotes] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  // Calculate operation bounding box from loaded data
  const operationBounds = useMemo(() => {
    const coords: Array<{ lat: number; lon: number }> = [];
    
    // Add data point coordinates (coordinates is [lng, lat] or [lat, lng] tuple)
    dataPoints.forEach(p => {
      if (p.coordinates && p.coordinates.length >= 2) {
        // Assuming coordinates are [lat, lng] based on common Pathfinder usage
        coords.push({ lat: p.coordinates[0], lon: p.coordinates[1] });
      }
    });
    
    // Add dwell location coordinates
    dwellLocations.forEach(d => {
      if (d.latitude && d.longitude) {
        coords.push({ lat: d.latitude, lon: d.longitude });
      }
    });
    
    return calculateBoundingBox(coords);
  }, [dataPoints, dwellLocations]);

  // Calculate center point for radius search
  const operationCenter = useMemo(() => {
    if (!operationBounds) return null;
    return {
      lat: (operationBounds.min_lat + operationBounds.max_lat) / 2,
      lon: (operationBounds.min_lon + operationBounds.max_lon) / 2,
    };
  }, [operationBounds]);

  // Check service availability on mount
  useEffect(() => {
    const checkService = async () => {
      try {
        const health = await checkHealth();
        setServiceAvailable(health.database_configured);
      } catch {
        setServiceAvailable(false);
      }
    };
    checkService();
  }, []);

  // Fetch full article when dialog opens
  useEffect(() => {
    if (selectedArticle?.id) {
      setLoadingArticle(true);
      setFullArticle(null);
      setNoteSaved(false); // Reset saved state for new article
      getArticle(selectedArticle.id)
        .then((article) => {
          setFullArticle(article as OverwatchArticle);
        })
        .catch((err) => {
          console.error('Failed to fetch full article:', err);
          // Fall back to the basic article data
          setFullArticle(selectedArticle);
        })
        .finally(() => {
          setLoadingArticle(false);
        });
    } else {
      setFullArticle(null);
    }
  }, [selectedArticle]);

  // Search handlers
  const handleKeywordSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const results = await searchArticles({
        query: searchQuery,
        categories: selectedCategory || undefined,
        days: daysBack,
        limit: 50,
      });
      setSearchResults(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedCategory, daysBack]);

  const handleAreaSearch = useCallback(async () => {
    if (!operationBounds) {
      setError('No operation area defined. Load device data first.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const results = await searchByArea({
        ...operationBounds,
        categories: selectedCategory ? [selectedCategory] : undefined,
        keywords: searchQuery || undefined,
        days: daysBack,
        limit: 100,
      });
      setSearchResults(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Area search failed');
    } finally {
      setIsLoading(false);
    }
  }, [operationBounds, selectedCategory, searchQuery, daysBack]);

  const handleRadiusSearch = useCallback(async () => {
    if (!operationCenter) {
      setError('No operation center defined. Load device data first.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const results = await searchByRadius({
        lat: operationCenter.lat,
        lon: operationCenter.lon,
        radiusKm: searchRadius,
        categories: selectedCategory || undefined,
        keywords: searchQuery || undefined,
        days: daysBack,
        limit: 50,
      });
      setSearchResults(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Radius search failed');
    } finally {
      setIsLoading(false);
    }
  }, [operationCenter, searchRadius, selectedCategory, searchQuery, daysBack]);

  // Extract place names from dwell locations (with geocoding if needed)
  const extractPlaceNames = useCallback(async () => {
    setIsExtractingPlaces(true);
    setError(null);
    
    try {
      const placeNames: string[] = [];
      const seenCoords = new Set<string>(); // Track unique coordinates
      const coordsToGeocode: Array<{ lat: number; lng: number; id: string }> = [];
      
      // First, collect existing place names from dwell locations
      dwellLocations.forEach((dwell, idx) => {
        // Check for existing place name
        const existingName = (dwell as { place_name?: string }).place_name || 
                            (dwell as { site_name?: string }).site_name ||
                            (dwell as { name?: string }).name;
        
        if (existingName && !existingName.match(/^-?\d+\.\d+,\s*-?\d+\.\d+$/) && 
            !existingName.match(/^Site\s+[A-Z]\d*$/i)) {
          // Valid place name exists
          placeNames.push(existingName);
        } else if (dwell.latitude && dwell.longitude) {
          // Round to ~100m precision to deduplicate nearby locations
          const coordKey = `${dwell.latitude.toFixed(3)},${dwell.longitude.toFixed(3)}`;
          
          if (!seenCoords.has(coordKey)) {
            seenCoords.add(coordKey);
            coordsToGeocode.push({
              lat: dwell.latitude,
              lng: dwell.longitude,
              id: `dwell-${idx}`
            });
          }
        }
      });
      
      // Limit geocoding to max 20 unique locations to avoid API overload
      const limitedCoords = coordsToGeocode.slice(0, 20);
      
      // Geocode coordinates that don't have place names
      if (limitedCoords.length > 0) {
        console.log(`[OverwatchIntel] Geocoding ${limitedCoords.length} unique locations (deduped from ${dwellLocations.length} dwells)...`);
        const geocodeResults = await batchReverseGeocode(limitedCoords);
        
        geocodeResults.forEach((name) => {
          if (name && name.trim()) {
            placeNames.push(name);
          }
        });
      }
      
      // Remove duplicates and filter out generic names
      const uniqueNames = Array.from(new Set(placeNames))
        .filter(name => name && name.length > 2)
        .slice(0, 20); // Limit to 20 places for search
      
      console.log(`[OverwatchIntel] Extracted ${uniqueNames.length} unique place names:`, uniqueNames);
      
      // Translate foreign place names to English and get context
      const translations = await translatePlaceNames(uniqueNames, true);
      
      // Combine original and English names (deduplicated) and store contexts
      const allNames = new Set<string>();
      const contexts = new Map<string, string>();
      
      translations.forEach(t => {
        allNames.add(t.original);
        if (t.context) {
          contexts.set(t.original, t.context);
        }
        if (t.english && t.english !== t.original) {
          allNames.add(t.english);
          if (t.context) {
            contexts.set(t.english, t.context);
          }
          console.log(`[OverwatchIntel] Translated: ${t.original} → ${t.english} (${t.detected_language})`);
          if (t.context) {
            console.log(`[OverwatchIntel] Context: ${t.context}`);
          }
        }
      });
      
      setPlaceContexts(contexts);
      
      const finalNames = Array.from(allNames).slice(0, 30); // Allow more names after translation
      console.log(`[OverwatchIntel] Final place names (with translations):`, finalNames);
      setExtractedPlaceNames(finalNames);
      
      return finalNames;
    } catch (e) {
      console.error('[OverwatchIntel] Failed to extract place names:', e);
      setError(e instanceof Error ? e.message : 'Failed to extract place names');
      return [];
    } finally {
      setIsExtractingPlaces(false);
    }
  }, [dwellLocations]);

  // Search by place names
  const handlePlacesSearch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Extract place names if not already done
      let names = extractedPlaceNames;
      if (names.length === 0) {
        names = await extractPlaceNames();
      }
      
      if (names.length === 0) {
        setError('No place names found. Load device data with dwell locations first.');
        return;
      }
      
      // Combine place names into a search query
      // Use OR logic by searching for each name
      const searchTerms = names.slice(0, 10).join(' OR ');
      console.log(`[OverwatchIntel] Searching for places: ${searchTerms}`);
      
      const results = await searchArticles({
        query: searchTerms,
        categories: selectedCategory || undefined,
        days: daysBack,
        limit: 100,
      });
      
      setSearchResults(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Places search failed');
    } finally {
      setIsLoading(false);
    }
  }, [extractedPlaceNames, extractPlaceNames, selectedCategory, daysBack]);

  const handleSearch = useCallback(() => {
    switch (searchMode) {
      case 'area':
        handleAreaSearch();
        break;
      case 'radius':
        handleRadiusSearch();
        break;
      case 'places':
        handlePlacesSearch();
        break;
      case 'keyword':
      default:
        handleKeywordSearch();
        break;
    }
  }, [searchMode, handleKeywordSearch, handleAreaSearch, handleRadiusSearch, handlePlacesSearch]);

  const colors = {
    accent: primaryColor,
    accentDark: theme.palette.primary.dark,
    secondary: theme.palette.secondary.main,
    secondaryDark: theme.palette.secondary.dark,
    text: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    border: theme.palette.divider,
    surface: isDark ? 'rgba(30, 32, 48, 0.8)' : 'rgba(248, 250, 252, 0.95)',
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
  };

  // Render article card
  const renderArticleCard = (article: OverwatchArticle, index: number) => (
    <Card
      key={article.id || index}
      sx={{
        mb: 1,
        bgcolor: isDark ? 'rgba(40, 44, 60, 0.6)' : 'rgba(255, 255, 255, 0.9)',
        border: `1px solid ${colors.border}`,
        '&:hover': {
          borderColor: colors.accent,
          bgcolor: isDark ? 'rgba(40, 44, 60, 0.8)' : 'rgba(255, 255, 255, 1)',
        },
        cursor: article.latitude && article.longitude ? 'pointer' : 'default',
      }}
      onClick={() => {
        // Zoom to article location when clicked
        if (article.latitude && article.longitude) {
          onZoomToLocation?.(article.latitude, article.longitude, 14);
        }
        onArticleClick?.(article);
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Title */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '0.8rem',
            color: colors.text,
            mb: 0.5,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {article.title}
        </Typography>
        
        {/* Meta info */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
          {/* Date */}
          <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.65rem' }}>
            {formatPubdate(article.pubdate)}
          </Typography>
          
          {/* Location */}
          {(article.city || article.country) && (
            <>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>•</Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.65rem' }}>
                {[article.city, article.country].filter(Boolean).join(', ')}
              </Typography>
            </>
          )}
          
          {/* Category chip */}
          {article.primary_category && (
            <Chip
              label={article.primary_category}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                bgcolor: alpha(getCategoryColor(article.primary_category), 0.15),
                color: getCategoryColor(article.primary_category),
                fontWeight: 600,
                ml: 'auto',
              }}
            />
          )}
        </Box>
        
        {/* Actions row */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, alignItems: 'center' }}>
          {/* Sentiment indicator */}
          {article.sentiment && (
            <Tooltip title={`Sentiment: ${article.sentiment}`}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: getSentimentColor(article.sentiment),
                }}
              />
            </Tooltip>
          )}
          
          {/* Zoom to location */}
          {article.latitude && article.longitude && (
            <Tooltip title="Show on map">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onZoomToLocation?.(article.latitude!, article.longitude!, 14);
                }}
                sx={{ p: 0.25, color: colors.textSecondary }}
              >
                <LocationIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* View full article details */}
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedArticle(article);
              }}
              sx={{ p: 0.25, color: colors.textSecondary }}
            >
              <InfoIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          
          {/* Open source link */}
          {article.source_link && (
            <Tooltip title="Open source">
              <IconButton
                size="small"
                component={Link}
                href={article.source_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                sx={{ p: 0.25, color: colors.textSecondary, ml: 'auto' }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // Count articles with valid locations
  const articlesWithLocation = searchResults?.articles.filter(a => a.latitude && a.longitude) || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isFullPanel ? 'transparent' : colors.surface,
        borderRadius: isFullPanel ? 0 : 2,
        border: isFullPanel ? 'none' : `1px solid ${colors.border}`,
        overflow: 'hidden',
        height: isFullPanel ? '100%' : 'auto',
      }}
    >
      {/* Header - only show when not in full panel mode */}
      {!isFullPanel && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderBottom: collapsed ? 'none' : `1px solid ${colors.border}`,
            cursor: 'pointer',
            '&:hover': { bgcolor: alpha(colors.accent, 0.05) },
          }}
          onClick={onToggleCollapse}
        >
          <NewsIcon sx={{ color: colors.accent, fontSize: '1.1rem' }} />
          <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.85rem', flex: 1 }}>
            Overwatch Intel
          </Typography>
          
          {/* Result count badge */}
          {searchResults && (
            <Chip
              label={searchResults.total_count}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: alpha(colors.accent, 0.15),
                color: colors.accent,
                fontWeight: 600,
              }}
            />
          )}
          
          <IconButton size="small" sx={{ color: colors.textSecondary, p: 0.5 }}>
            {collapsed ? <ExpandIcon sx={{ fontSize: '1rem' }} /> : <CollapseIcon sx={{ fontSize: '1rem' }} />}
          </IconButton>
        </Box>
      )}

      {/* Content */}
      <Collapse in={isFullPanel || !collapsed} sx={{ flex: isFullPanel ? 1 : 'none', minHeight: 0, overflow: isFullPanel ? 'hidden' : 'visible', display: isFullPanel ? 'flex' : 'block', flexDirection: 'column', '& .MuiCollapse-wrapper': { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }, '& .MuiCollapse-wrapperInner': { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } }}>
        <Box sx={{ p: 1.5, pt: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: isFullPanel ? 'hidden' : 'visible' }}>
          {/* Service status */}
          {serviceAvailable === false && (
            <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}>
              Overwatch database not configured
            </Alert>
          )}

          {/* Search mode selector */}
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, mt: 1, alignItems: 'center' }}>
            <Chip
              label="Keyword"
              size="small"
              onClick={() => setSearchMode('keyword')}
              sx={{
                flex: 1,
                fontSize: '0.7rem',
                bgcolor: searchMode === 'keyword' ? colors.accent : 'transparent',
                color: searchMode === 'keyword' ? theme.palette.primary.contrastText : colors.textSecondary,
                border: `1px solid ${searchMode === 'keyword' ? colors.accent : colors.border}`,
                '&:hover': { bgcolor: searchMode === 'keyword' ? colors.accent : alpha(colors.accent, 0.1) },
              }}
            />
            <Chip
              label="Area Search"
              size="small"
              icon={<GlobeIcon sx={{ fontSize: 14 }} />}
              onClick={() => setSearchMode('area')}
              disabled={!operationBounds}
              sx={{
                flex: 1,
                fontSize: '0.7rem',
                bgcolor: searchMode === 'area' ? colors.accent : 'transparent',
                color: searchMode === 'area' ? theme.palette.primary.contrastText : colors.textSecondary,
                border: `1px solid ${searchMode === 'area' ? colors.accent : colors.border}`,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            <Chip
              label="Radius"
              size="small"
              icon={<MyLocationIcon sx={{ fontSize: 14 }} />}
              onClick={() => setSearchMode('radius')}
              disabled={!operationCenter}
              sx={{
                flex: 1,
                fontSize: '0.7rem',
                bgcolor: searchMode === 'radius' ? colors.accent : 'transparent',
                color: searchMode === 'radius' ? theme.palette.primary.contrastText : colors.textSecondary,
                border: `1px solid ${searchMode === 'radius' ? colors.accent : colors.border}`,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            <Chip
              label="Places"
              size="small"
              icon={<PlaceIcon sx={{ fontSize: 14 }} />}
              onClick={() => setSearchMode('places')}
              disabled={dwellLocations.length === 0}
              sx={{
                flex: 1,
                fontSize: '0.7rem',
                bgcolor: searchMode === 'places' ? colors.accent : 'transparent',
                color: searchMode === 'places' ? theme.palette.primary.contrastText : colors.textSecondary,
                border: `1px solid ${searchMode === 'places' ? colors.accent : colors.border}`,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
            <Tooltip 
              title={
                <Box sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                  <strong>Search Modes:</strong><br/>
                  • <strong>Keyword:</strong> Search by text<br/>
                  • <strong>Area:</strong> Search within your track data bounding box<br/>
                  • <strong>Radius:</strong> Search within distance from center<br/>
                  • <strong>Places:</strong> Search by dwell location names
                </Box>
              } 
              placement="bottom" 
              arrow
            >
              <InfoIcon sx={{ fontSize: 16, color: colors.textSecondary, cursor: 'help', ml: 0.5 }} />
            </Tooltip>
          </Box>

          {/* Search input */}
          <TextField
            fullWidth
            size="small"
            placeholder={searchMode === 'keyword' ? 'Search news & intel...' : searchMode === 'places' ? 'Additional keywords (optional)...' : 'Filter by keywords (optional)...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            disabled={isLoading}
            InputProps={{
              sx: {
                bgcolor: isDark ? 'rgba(20, 22, 35, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                color: colors.text,
                fontSize: '0.8rem',
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: colors.accent },
              },
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Filters">
                    <IconButton
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                      sx={{ color: showFilters ? colors.accent : colors.textSecondary }}
                    >
                      <FilterIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={handleSearch}
                    disabled={isLoading || (searchMode === 'keyword' && !searchQuery.trim())}
                    sx={{ color: colors.accent }}
                  >
                    {isLoading ? <CircularProgress size={16} /> : <SearchIcon sx={{ fontSize: '1rem' }} />}
                  </IconButton>
                </Box>
              ),
            }}
          />

          {/* Filters panel */}
          <Collapse in={showFilters}>
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: alpha(colors.border, 0.3), borderRadius: 1 }}>
              {/* Category filter */}
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="">
                    <em>All Categories</em>
                  </MenuItem>
                  {COMMON_CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Days back slider */}
              <Box sx={{ px: 1 }}>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  Days to search: {daysBack}
                </Typography>
                <Slider
                  value={daysBack}
                  onChange={(_, value) => setDaysBack(value as number)}
                  min={1}
                  max={365}
                  size="small"
                  sx={{ color: colors.accent }}
                />
              </Box>

              {/* Radius slider (for radius mode) */}
              {searchMode === 'radius' && (
                <Box sx={{ px: 1, mt: 1 }}>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    Search radius: {searchRadius} km
                  </Typography>
                  <Slider
                    value={searchRadius}
                    onChange={(_, value) => setSearchRadius(value as number)}
                    min={5}
                    max={500}
                    size="small"
                    sx={{ color: colors.accent }}
                  />
                </Box>
              )}
            </Box>
          </Collapse>

          {/* Quick search buttons for area mode */}
          {searchMode === 'area' && operationBounds && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<GlobeIcon sx={{ fontSize: 14 }} />}
                onClick={handleAreaSearch}
                disabled={isLoading}
                sx={{
                  flex: 1,
                  fontSize: '0.75rem',
                  py: 0.75,
                  bgcolor: colors.accent,
                  '&:hover': { bgcolor: colors.accentDark },
                }}
              >
                Search Operation Area
              </Button>
            </Box>
          )}

          {/* Places search mode UI */}
          {searchMode === 'places' && (
            <Box sx={{ mt: 1.5 }}>
              {/* Extract places button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={isExtractingPlaces ? <CircularProgress size={14} /> : <PlaceIcon sx={{ fontSize: 14 }} />}
                onClick={extractPlaceNames}
                disabled={isExtractingPlaces || dwellLocations.length === 0}
                sx={{
                  width: '100%',
                  fontSize: '0.75rem',
                  py: 0.5,
                  mb: 1,
                  borderColor: colors.accent,
                  color: colors.accent,
                }}
              >
                {isExtractingPlaces ? 'Extracting...' : `Extract Places from ${dwellLocations.length} Dwell Locations`}
              </Button>
              
              {/* Show extracted place names with AI context */}
              {extractedPlaceNames.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: 0.5 }}>
                    Found {extractedPlaceNames.length} place names (hover for context):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {extractedPlaceNames.slice(0, 10).map((name, idx) => {
                      const context = placeContexts.get(name);
                      return (
                        <Tooltip 
                          key={idx} 
                          title={context || 'No context available'} 
                          placement="top"
                          arrow
                        >
                          <Chip
                            label={name}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 20,
                              bgcolor: context ? alpha(colors.accent, 0.15) : alpha(colors.accent, 0.1),
                              color: colors.accent,
                              cursor: 'help',
                              '&:hover': {
                                bgcolor: alpha(colors.accent, 0.25),
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                    {extractedPlaceNames.length > 10 && (
                      <Chip
                        label={`+${extractedPlaceNames.length - 10} more`}
                        size="small"
                        sx={{
                          fontSize: '0.65rem',
                          height: 20,
                          bgcolor: alpha(colors.border, 0.5),
                          color: colors.textSecondary,
                        }}
                      />
                    )}
                  </Box>
                  
                  {/* Show context summary if available - collapsible */}
                  {placeContexts.size > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        onClick={() => setShowPlaceContext(!showPlaceContext)}
                        startIcon={showPlaceContext ? <CollapseIcon /> : <ExpandIcon />}
                        sx={{ 
                          fontSize: '0.7rem', 
                          color: colors.textSecondary,
                          textTransform: 'none',
                          p: 0.5,
                          minWidth: 0,
                        }}
                      >
                        📍 Location Context ({placeContexts.size} places)
                      </Button>
                      <Collapse in={showPlaceContext}>
                        <Box sx={{ 
                          p: 1, 
                          bgcolor: alpha(colors.border, 0.2), 
                          borderRadius: 1,
                          maxHeight: 200,
                          overflow: 'auto',
                          mt: 0.5,
                        }}>
                          {extractedPlaceNames.map((name, idx) => {
                            const context = placeContexts.get(name);
                            if (!context) return null;
                            return (
                              <Typography key={idx} variant="caption" sx={{ color: colors.text, display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                                <strong>{name}:</strong> {context}
                              </Typography>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Search button */}
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                onClick={handlePlacesSearch}
                disabled={isLoading || (extractedPlaceNames.length === 0 && !isExtractingPlaces)}
                sx={{
                  width: '100%',
                  fontSize: '0.75rem',
                  py: 0.75,
                  bgcolor: colors.secondary,
                  '&:hover': { bgcolor: colors.secondaryDark },
                }}
              >
                {isLoading ? 'Searching...' : 'Search News by Place Names'}
              </Button>
            </Box>
          )}

          {/* Error display */}
          {error && (
            <Alert
              severity="error"
              sx={{ mt: 1.5, py: 0.5, fontSize: '0.75rem' }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Loaded articles indicator */}
          {loadedArticles.length > 0 && (
            <Box sx={{ 
              mt: 1.5, 
              p: 1, 
              bgcolor: alpha(colors.accent, 0.1), 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon sx={{ fontSize: 16, color: colors.accent }} />
                <Typography variant="caption" sx={{ color: colors.text, fontWeight: 600 }}>
                  {loadedArticles.length} articles on map
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={onClearArticles}
                sx={{ 
                  fontSize: '0.65rem', 
                  py: 0.25, 
                  px: 1,
                  color: colors.textSecondary,
                  '&:hover': { bgcolor: alpha(colors.accent, 0.1) },
                }}
              >
                Clear
              </Button>
            </Box>
          )}

          {/* Results */}
          {searchResults && (
            <Box sx={{ mt: 1.5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
                  {searchResults.total_count} articles found
                  {articlesWithLocation.length > 0 && articlesWithLocation.length < searchResults.total_count && (
                    <span style={{ opacity: 0.7 }}> ({articlesWithLocation.length} with location)</span>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {/* Load to Map button */}
                  {articlesWithLocation.length > 0 && onLoadArticlesToMap && (
                    <Tooltip title={`Load ${articlesWithLocation.length} articles to map`}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onLoadArticlesToMap(articlesWithLocation)}
                        sx={{
                          fontSize: '0.65rem',
                          py: 0.25,
                          px: 1,
                          bgcolor: colors.accent,
                          '&:hover': { bgcolor: colors.accentDark },
                        }}
                      >
                        <LocationIcon sx={{ fontSize: 12, mr: 0.5 }} />
                        Load to Map
                      </Button>
                    </Tooltip>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setSearchResults(null)}
                    sx={{ p: 0.25, color: colors.textSecondary }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  ...(isFullPanel ? {} : { maxHeight: 400 }),
                  overflowY: 'auto',
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: colors.border, borderRadius: 2 },
                }}
              >
                {searchResults.articles.map((article, idx) => renderArticleCard(article, idx))}
                
                {searchResults.articles.length === 0 && (
                  <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', py: 2 }}>
                    No articles found. Try different search terms or expand your date range.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Empty state */}
          {!searchResults && !isLoading && (
            <Box sx={{ 
              mt: 2, 
              textAlign: 'center', 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <ArticleIcon sx={{ fontSize: 48, color: colors.accent, opacity: 0.4, mb: 1 }} />
              <Typography variant="body2" sx={{ color: colors.text, fontSize: '0.85rem', fontWeight: 500 }}>
                Search Overwatch Intelligence
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mt: 0.5, maxWidth: 250 }}>
                {operationBounds
                  ? 'Use "Area Search" to find news & intel for your operation area'
                  : 'Enter keywords or load device data to enable geographic search'}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Article Details Dialog */}
      <Dialog
        open={!!selectedArticle}
        onClose={() => setSelectedArticle(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.surface,
            backgroundImage: 'none',
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        {selectedArticle && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              justifyContent: 'space-between',
              pb: 1,
              borderBottom: `1px solid ${colors.border}`,
            }}>
              <Box sx={{ pr: 2, flex: 1 }}>
                <Typography variant="h6" sx={{ color: colors.text, fontWeight: 600, lineHeight: 1.3 }}>
                  {selectedArticle.title || 'Article Details'}
                </Typography>
                {selectedArticle.source && (
                  <Typography variant="caption" sx={{ color: colors.textSecondary, mt: 0.5, display: 'block' }}>
                    Source: {selectedArticle.source}
                  </Typography>
                )}
              </Box>
              <IconButton
                onClick={() => setSelectedArticle(null)}
                size="small"
                sx={{ color: colors.textSecondary, mt: -0.5 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {/* Metadata row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {selectedArticle.pubdate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DateIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      {formatPubdate(selectedArticle.pubdate)}
                    </Typography>
                  </Box>
                )}
                {(selectedArticle.category || selectedArticle.primary_category) && (
                  <Chip
                    icon={<CategoryIcon sx={{ fontSize: 14 }} />}
                    label={selectedArticle.category || selectedArticle.primary_category}
                    size="small"
                    sx={{
                      bgcolor: alpha(getCategoryColor(selectedArticle.category || selectedArticle.primary_category || ''), 0.2),
                      color: getCategoryColor(selectedArticle.category || selectedArticle.primary_category || ''),
                      borderColor: getCategoryColor(selectedArticle.category || selectedArticle.primary_category || ''),
                      fontSize: '0.75rem',
                    }}
                    variant="outlined"
                  />
                )}
                {selectedArticle.sentiment && (
                  <Chip
                    icon={<SentimentIcon sx={{ fontSize: 14 }} />}
                    label={selectedArticle.sentiment}
                    size="small"
                    sx={{
                      bgcolor: alpha(getSentimentColor(selectedArticle.sentiment), 0.2),
                      color: getSentimentColor(selectedArticle.sentiment),
                      borderColor: getSentimentColor(selectedArticle.sentiment),
                      fontSize: '0.75rem',
                    }}
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Location */}
              {(selectedArticle.latitude && selectedArticle.longitude) && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <LocationIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
                    <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
                      Location
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: colors.text, pl: 2.5 }}>
                    {selectedArticle.latitude.toFixed(4)}, {selectedArticle.longitude.toFixed(4)}
                    {(selectedArticle.location_name || selectedArticle.city || selectedArticle.country) && 
                      ` (${[selectedArticle.location_name, selectedArticle.city, selectedArticle.country].filter(Boolean).join(', ')})`}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<MyLocationIcon />}
                    onClick={() => {
                      onZoomToLocation?.(selectedArticle.latitude!, selectedArticle.longitude!, 14);
                      setSelectedArticle(null);
                    }}
                    sx={{ ml: 2, mt: 0.5, textTransform: 'none' }}
                  >
                    Zoom to location
                  </Button>
                </Box>
              )}

              <Divider sx={{ my: 2, borderColor: colors.border }} />

              {/* Content/Summary */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500, mb: 1 }}>
                  {(fullArticle?.article_text || selectedArticle.article_text) ? 'Full Article' : 'Content'}
                </Typography>
                {loadingArticle ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      Loading full article...
                    </Typography>
                  </Box>
                ) : (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: colors.text, 
                      lineHeight: 1.6,
                      maxHeight: 300,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {fullArticle?.article_text || fullArticle?.summary || selectedArticle.summary || selectedArticle.title || 'No content available.'}
                  </Typography>
                )}
              </Box>

              {/* Entities if available */}
              {(fullArticle as unknown as { entities?: { attacker?: string; target?: string; arrested?: string } })?.entities && 
               Object.values((fullArticle as unknown as { entities: { attacker?: string; target?: string; arrested?: string } }).entities).some(v => v) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500, mb: 1 }}>
                    Mentioned Entities
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Object.entries((fullArticle as unknown as { entities: { attacker?: string; target?: string; arrested?: string } }).entities)
                      .filter(([, value]) => value)
                      .map(([key, value], idx) => (
                      <Chip
                        key={idx}
                        label={`${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`}
                        size="small"
                        sx={{
                          bgcolor: key === 'attacker' ? alpha(colors.error, 0.15) : 
                                   key === 'target' ? alpha(colors.warning, 0.15) : 
                                   alpha(colors.info, 0.15),
                          color: key === 'attacker' ? colors.error : 
                                 key === 'target' ? colors.warning : 
                                 colors.info,
                          fontSize: '0.75rem',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Keywords if available */}
              {(fullArticle as unknown as { keywords?: string })?.keywords && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500, mb: 1 }}>
                    Keywords
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text, fontSize: '0.85rem' }}>
                    {(fullArticle as unknown as { keywords: string }).keywords}
                  </Typography>
                </Box>
              )}

              {/* Action buttons */}
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.border}`, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* Save to Notes button */}
                {onSaveArticleToNotes && availableDevices.length > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={savingToNotes ? <CircularProgress size={14} color="inherit" /> : <SaveNoteIcon />}
                    disabled={savingToNotes || noteSaved}
                    onClick={async () => {
                      setSavingToNotes(true);
                      try {
                        const success = await onSaveArticleToNotes(selectedArticle);
                        if (success) {
                          setNoteSaved(true);
                          // Reset after a few seconds
                          setTimeout(() => setNoteSaved(false), 3000);
                        }
                      } finally {
                        setSavingToNotes(false);
                      }
                    }}
                    sx={{ 
                      textTransform: 'none',
                      bgcolor: noteSaved ? 'success.main' : 'primary.main',
                    }}
                  >
                    {noteSaved ? '✓ Saved to Intel Packet' : 'Save to Intel Packet'}
                  </Button>
                )}
                
                {/* Source link */}
                {selectedArticle.source_link && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    component={Link}
                    href={selectedArticle.source_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: 'none' }}
                  >
                    View Original Article
                  </Button>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OverwatchIntel;

